import Skeleton from "@mui/material/Skeleton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BackButton } from "src/components/Default/LekUI/BackButton";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import TipTapEditor from "src/components/TipTapEditor/TipTapEditor";
import { useGetUserNote } from "src/hooks/useFolders";
import newApi from "src/newApi";
import "src/styles/notes.scss";
import * as Yup from "yup";

interface FolderNoteEditProps {
	noteId: string;
	onCancel: () => void;
	onSaved: () => void;
}

export function FolderNoteEdit({
	noteId,
	onCancel,
	onSaved,
}: FolderNoteEditProps) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [editorError, setEditorError] = useState("");

	const { data: noteData, isLoading } = useGetUserNote(noteId);

	const mutation = useMutation<
		unknown,
		unknown,
		{ note: string; name: string }
	>({
		mutationFn: ({ note, name }) =>
			newApi.updateUserNote(noteId, { note, name }),

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["user-note"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-notes"],
			});
			queryClient.invalidateQueries({
				queryKey: ["folder-items"],
			});
			onSaved();
		},

		onError: () => {
			toast.error(t("note.saveError", "Nie udało się zapisać notatki"));
		},
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			title: noteData?.name || "",
		},
		validationSchema: Yup.object().shape({
			title: Yup.string()
				.required(t("validation.required", "To pole jest wymagane"))
				.max(255, t("validation.maxChars", "Maksymalnie 255 znaków")),
		}),
		onSubmit: () => {},
	});

	const handleSave = async (htmlString: string) => {
		formik.setFieldTouched("title", true);
		const errors = await formik.validateForm();
		if (errors.title) return;

		const isEmpty =
			!htmlString || htmlString.replace(/<[^>]*>/g, "").trim() === "";
		if (isEmpty) {
			setEditorError(
				t("note.emptyContent", "Treść notatki nie może być pusta"),
			);
			return;
		}

		mutation.mutate({ note: htmlString, name: formik.values.title });
	};

	if (isLoading) {
		return (
			<div className="content-box border-medium-primary border-radius-8 flex flex-col gap-24">
				<div className="flex flex-col gap-12 w-100 pb-16 border-bottom-2">
					<Skeleton variant="text" width={120} height={20} />
					<Skeleton variant="rounded" width={350} height={44} />
				</div>
				<Skeleton variant="rounded" height={44} />
				<Skeleton variant="rounded" height={200} />
			</div>
		);
	}

	return (
		<div className="content-box border-medium-primary border-radius-8 flex flex-col gap-24">
			<div className="w-100 flex flex-col gap-12 pb-16 border-bottom-2">
				<BackButton onClick={onCancel} testId="folder-note-edit-back" />
				<span className="size-14">{t("note.titleLabel", "Nazwa notatki")}</span>
				<TextFieldGrey
					variant="filled"
					name="title"
					data-test-id="folder-note-edit-title"
					value={formik.values.title}
					onChange={formik.handleChange}
					className="w-100 max-w-350"
					autoFocus
					error={formik.touched.title && Boolean(formik.errors.title)}
					helperText={
						formik.touched.title ? (formik.errors.title as string) : undefined
					}
					slotProps={{ htmlInput: { maxLength: 255 } }}
				/>
			</div>
			<TipTapEditor
				onSubmit={handleSave}
				submitText={t("note.save", "Zapisz zmiany")}
				cancelText={t("note.cancel", "Anuluj")}
				onCancel={onCancel}
				placeholder=""
				className="note-editor"
				initialHtml={noteData?.note || ""}
				error={!!editorError}
				helperText={editorError}
				onUpdate={() => {
					if (editorError) setEditorError("");
				}}
			/>
		</div>
	);
}
